// Sentry init — called once at app boot from main.jsx. Silently
// no-ops when VITE_SENTRY_DSN isn't set so local dev without the
// env var doesn't error.
//
// PII discipline (O03 hard rule): we attach the user's npub to
// Sentry.setUser({ id: ... }) elsewhere because npub is public.
// We NEVER pass nsec, ncryptsec, passphrases, or any private key
// material through Sentry — not in event payloads, breadcrumbs,
// tags, or extras.

import * as Sentry from '@sentry/react'

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) return false

  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'unknown'
  // Vercel exposes VERCEL_GIT_COMMIT_SHA at build time, but Vite only
  // bundles env vars prefixed with VITE_. If we want the release tag,
  // either set VITE_COMMIT_SHA explicitly in Vercel or wire it up via
  // a vite define. Falls back to undefined if absent.
  const release = import.meta.env.VITE_COMMIT_SHA || undefined

  Sentry.init({
    dsn,
    environment,
    release,
    // Sample 10% of transactions for perf monitoring. Errors are
    // always sampled at 100% — the rate below only affects spans.
    tracesSampleRate: 0.1,
    // Session replay disabled — privacy + cost.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // Don't auto-attach IP, cookies, headers, request bodies, etc.
    // We pass npub explicitly via setUser; nothing else.
    sendDefaultPii: false,
    // Drop user-rejection errors. Same pattern AccountModal uses to
    // translate NIP-07 rejections into friendly "Signing canceled"
    // copy — those aren't bugs and shouldn't burn quota.
    beforeSend(event, hint) {
      const message = String(
        hint?.originalException?.message || event?.message || '',
      )
      if (/reject|denied|cancel/i.test(message)) return null
      return event
    },
  })
  return true
}
